import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const palettes = [
  { name: "Classic Noir", a: "#141414", b: "#FDFBF7" },
  { name: "Blush Ballet", a: "#F06BA8", b: "#FDFBF7" },
  { name: "Champagne Gold", a: "#D4AF37", b: "#FDFBF7" },
  { name: "Coral Crush", a: "#F26D7D", b: "#FFF3EC" },
  { name: "Midnight Sky", a: "#0A0B1A", b: "#D4AF37" },
  { name: "Lavender Dream", a: "#9B7EDE", b: "#FDFBF7" },
  { name: "Emerald Estate", a: "#0E5F45", b: "#FDFBF7" },
  { name: "Ruby Romance", a: "#8E1F2F", b: "#FDFBF7" },
];

const sizes = [
  { label: "Petite", tile: 56 },
  { label: "Classic", tile: 96 },
  { label: "Grand", tile: 160 },
];

export default function Checkered() {
  const [palette, setPalette] = useState(palettes[0]);
  const [size, setSize] = useState(sizes[1]);

  return (
    <section id="checkered" data-testid="checkered-section" className="relative bg-ivory py-32 sm:py-44 overflow-hidden">
      <div className="absolute -top-20 right-0 w-[480px] h-[480px] rounded-full bg-blush/10 blur-[120px]" aria-hidden="true" />
      <div className="max-w-[1200px] mx-auto px-6 sm:px-10 relative">
        <FadeUp className="mb-6">
          <p className="text-[12px] tracking-[0.45em] uppercase text-coral">Checkered Dancefloors!</p>
        </FadeUp>
        <h2 className="font-serif font-light text-ink leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-6">
          <MaskedLines inView lines={[<>…now in all <span className="italic text-coral">colors and sizes</span></>]} />
        </h2>
        <FadeUp delay={0.1} className="mb-14 sm:mb-16">
          <p className="text-ink/60 text-base max-w-xl">
            The possibilities are <span className="font-quote italic text-ink">endless</span> —
            mix any two shades, pick your square, and we'll lay it wall to wall.
          </p>
        </FadeUp>

        <div className="grid lg:grid-cols-[1fr_340px] gap-10 lg:gap-16 items-start">
          <FadeUp>
            <div
              data-testid="checkered-floor-preview"
              className="relative h-[320px] sm:h-[420px] rounded-[2rem] overflow-hidden shadow-[0_40px_90px_-30px_rgba(45,40,51,0.4)]"
              style={{ perspective: "900px", background: palette.b }}
            >
              <AnimatePresence>
                <motion.div
                  key={`${palette.name}-${size.tile}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  className="absolute left-1/2 top-[8%] w-[220%] h-[300%] -translate-x-1/2"
                  style={{
                    transform: "translateX(-50%) rotateX(58deg)",
                    transformOrigin: "top center",
                    backgroundImage: `conic-gradient(${palette.a} 0 25%, ${palette.b} 0 50%, ${palette.a} 0 75%, ${palette.b} 0 100%)`,
                    backgroundSize: `${size.tile * 2}px ${Math.round((size.tile * 2) / Math.cos((58 * Math.PI) / 180))}px`,
                  }}
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-b from-ivory/40 via-transparent to-midnight/25 pointer-events-none" />
              <div className="absolute bottom-5 left-6 px-4 py-1.5 rounded-full bg-midnight/45 backdrop-blur-md pointer-events-none">
                <p data-testid="checkered-current-label" className="text-ivory text-[11px] tracking-[0.3em] uppercase">
                  {palette.name} · {size.label}
                </p>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.15}>
            <p className="text-[11px] tracking-[0.4em] uppercase text-ink/50 mb-5">Pick your shades</p>
            <div className="grid grid-cols-4 gap-4 mb-10">
              {palettes.map((p, i) => (
                <button
                  key={p.name}
                  data-testid={`checkered-swatch-${i}`}
                  onClick={() => setPalette(p)}
                  aria-label={p.name}
                  title={p.name}
                  className={`relative w-14 h-14 rounded-full transition-transform duration-300 hover:scale-110 ${
                    palette.name === p.name ? "ring-2 ring-offset-2 ring-coral ring-offset-ivory scale-110" : ""
                  }`}
                  style={{
                    backgroundImage: `conic-gradient(${p.a} 0 25%, ${p.b} 0 50%, ${p.a} 0 75%, ${p.b} 0 100%)`,
                    boxShadow: "inset 0 0 0 1px rgba(45,40,51,0.12)",
                  }}
                />
              ))}
            </div>

            <p className="text-[11px] tracking-[0.4em] uppercase text-ink/50 mb-5">Pick your square</p>
            <div className="flex gap-3 mb-10">
              {sizes.map((s) => (
                <button
                  key={s.label}
                  data-testid={`checkered-size-${s.label.toLowerCase()}`}
                  onClick={() => setSize(s)}
                  className={`px-6 py-2.5 rounded-full text-[12px] tracking-[0.2em] uppercase transition-colors duration-300 ${
                    size.label === s.label
                      ? "bg-ink text-ivory"
                      : "bg-transparent text-ink/60 ring-1 ring-ink/20 hover:ring-ink/50"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <p className="font-quote italic text-ink/70 text-lg leading-relaxed">
              Two shades. Three squares. One floor nobody forgets.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
